import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const UTMContext = createContext();

export const useUTM = () => {
  const context = useContext(UTMContext);
  if (!context) {
    throw new Error('useUTM must be used within a UTMProvider');
  }
  return context;
};

const STORAGE_KEY = 'workingdom_utm_params';

// Safe localStorage getter
const getStoredParams = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading UTM from localStorage:', error);
    return {};
  }
};

// Safe localStorage setter
const setStoredParams = (params) => {
  try {
    if (params && Object.keys(params).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    }
  } catch (error) {
    console.error('Error saving UTM to localStorage:', error);
  }
};

export const UTMProvider = ({ children }) => {
  // Synchronous initialization from localStorage guarantees instant availability on page refresh
  const [utmParams, setUtmParams] = useState(() => getStoredParams());
  const location = useLocation();

  // Extract UTM and tracking parameters from URL
  const extractUTMParams = useCallback((searchString) => {
    if (!searchString) return {};
    const urlParams = new URLSearchParams(searchString.startsWith('?') ? searchString : `?${searchString}`);
    const extracted = {};

    // Standard UTM and ad tracking parameters (including common typos)
    const trackedKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_campaign_id',
      'campaign_id',
      'campaignid',
      'cmapign_id',
      'utm_term',
      'utm_content',
      'gclid',
      'fbclid'
    ];

    trackedKeys.forEach((key) => {
      const val = urlParams.get(key);
      if (val) {
        extracted[key] = val;
      }
    });

    return extracted;
  }, []);

  // Update UTM parameters when URL changes while strictly preserving stored parameters across page navigation
  useEffect(() => {
    const currentUTM = extractUTMParams(location.search);

    if (Object.keys(currentUTM).length > 0) {
      // New UTM parameters detected in URL - merge with existing and persist
      setUtmParams((prev) => {
        const updated = { ...prev, ...currentUTM };
        setStoredParams(updated);
        console.log("🔍 UTM Context - Captured new UTM params:", updated);
        return updated;
      });
    } else {
      // No UTM parameters in current URL (user changed pages or refreshed)
      // Retain existing UTM parameters from state or localStorage
      const stored = getStoredParams();
      if (Object.keys(stored).length > 0) {
        setUtmParams(stored);
        console.log("🔍 UTM Context - Retaining UTM params across navigation/refresh:", stored);
      }
    }
  }, [location.search, extractUTMParams]);

  // Always returns active UTM params, falling back to localStorage if state is empty
  const getUTMParams = useCallback(() => {
    if (utmParams && Object.keys(utmParams).length > 0) {
      return utmParams;
    }
    return getStoredParams();
  }, [utmParams]);

  // Clear UTM parameters if needed
  const clearUTMParams = useCallback(() => {
    setUtmParams({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Check if active UTM parameters exist
  const hasUTMParams = useCallback(() => {
    const params = getUTMParams();
    return Object.keys(params).length > 0;
  }, [getUTMParams]);

  // Append UTM parameters to any internal link
  const appendUTMToUrl = useCallback((baseUrl) => {
    const params = getUTMParams();
    if (!params || Object.keys(params).length === 0) {
      return baseUrl;
    }

    try {
      const url = new URL(baseUrl, window.location.origin);
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        }
      });
      return url.pathname + url.search;
    } catch {
      return baseUrl;
    }
  }, [getUTMParams]);

  const preserveUTM = useCallback(() => {
    const params = getUTMParams();
    if (Object.keys(params).length > 0) {
      setStoredParams(params);
    }
  }, [getUTMParams]);

  return (
    <UTMContext.Provider
      value={{
        utmParams,
        getUTMParams,
        clearUTMParams,
        hasUTMParams,
        appendUTMToUrl,
        preserveUTM,
      }}
    >
      {children}
    </UTMContext.Provider>
  );
};